// Need to import React for the useEffect
import React, { useCallback, useRef, useState } from "react"
import {
  Animated,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { Image } from "expo-image"
import * as NavigationBar from "expo-navigation-bar"
import { useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { SidekickView } from "src/components/SidekickView"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { backOrToRoot } from "src/utils/routerHelpers"

/**
 * CrisisLineScreen - A full-screen crisis line display using SidekickView.
 *
 * Features:
 * - Veterans Crisis Line information
 * - Phone, text, and chat links
 * - Avatar overlay with speech integration
 * - Talk/Type/Menu button cluster
 */
export default function CrisisLineScreen() {
  const insets = useSafeAreaInsets()
  const { theme, navTheme } = useAppTheme()
  const { effectiveTopInset } = useStatusBarCompensation()

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  const controlsOpacity = useRef(new Animated.Value(1)).current

  const [controlsVisible, setControlsVisible] = useState(true)

  // Handle Android navigation bar
  React.useEffect(() => {
    if (Platform.OS === "android") {
      NavigationBar.setBackgroundColorAsync(theme.colors.palette.angry100)
      NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
    }

    return () => {
      if (Platform.OS === "android") {
        NavigationBar.setBackgroundColorAsync(theme.colors.background)
        NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
      }
    }
  }, [theme.colors.background, theme.colors.palette.angry100, navTheme.dark])

  // Handle opening URLs
  const handlePress = useCallback((url: string) => {
    Linking.openURL(url)
  }, [])

  // Toggle controls visibility when tapping the content
  const handleContentPress = useCallback(() => {
    const newVisible = !controlsVisible
    setControlsVisible(newVisible)
    Animated.timing(controlsOpacity, {
      toValue: newVisible ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start()
  }, [controlsVisible, controlsOpacity])

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.palette.angry100 }]}>
      <StatusBar style="dark" />

      <SidekickView
        showAvatar
        autoShowAvatarOnSpeech
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground={false}
        controlsOpacity={controlsOpacity}
        controlsVisible={controlsVisible}
        onContentPress={handleContentPress}
        riveOwner="video"
        controlsConfig={{ type: "none" }}
        style={{ backgroundColor: theme.colors.palette.angry100 }}
      >
        {/* Close button - positioned above the content */}
        <Animated.View
          style={[
            styles.closeButton,
            {
              top: effectiveTopInset + 22,
              right: insets.right + 12,
              opacity: controlsOpacity,
            },
          ]}
          pointerEvents={controlsVisible ? "auto" : "none"}
        >
          <TouchableOpacity
            onPress={() => backOrToRoot()}
            style={[styles.closeButtonInner, { backgroundColor: theme.colors.buttonBackground }]}
            accessibilityRole="button"
            accessibilityLabel="Close crisis line"
          >
            <MaterialIcons name="close" size={20} color={theme.colors.buttonText} />
          </TouchableOpacity>
        </Animated.View>
        {/* Crisis Line Content */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingTop: 60, // Space for close button (SidekickView provides insets.top)
              paddingBottom: 140, // Space for ButtonCluster
            },
          ]}
        >
          <Image
            source={require("src/components/CrisisLine/veterans-crisis-line-logo.png")}
            style={styles.logo}
            contentFit="contain"
          />
          <Text style={[styles.title, { color: theme.colors.palette.angry500 }]}>
            Talk to a Veteran Crisis Line responder now
          </Text>
          <Text style={[styles.description, { color: theme.colors.text }]}>
            <Text style={styles.bold}>We&apos;re here anytime, day or night - 24/7.</Text> If you
            Veteran in crisis or concerned about one, connect with our caring, qualified responders
            for confidential help. Many of them are Veterans themselves.
          </Text>

          <View style={styles.linksContainer}>
            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => handlePress("tel:8002738255")}
            >
              <MaterialIcons
                name="phone"
                size={24}
                color={theme.colors.palette.primary600}
                style={styles.linkIcon}
              />
              <Text style={[styles.linkText, { color: theme.colors.palette.primary600 }]}>
                Call <Text style={styles.bold}>800-273-8255</Text> and press{" "}
                <Text style={styles.bold}>1</Text>
              </Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.linkButton} onPress={() => handlePress("sms:838255")}>
              <MaterialIcons
                name="textsms"
                size={24}
                color={theme.colors.palette.primary600}
                style={styles.linkIcon}
              />
              <Text style={[styles.linkText, { color: theme.colors.palette.primary600 }]}>
                Text <Text style={styles.bold}>838255</Text>
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => handlePress("https://www.veteranscrisisline.net/chat")}
            >
              <MaterialIcons
                name="chat"
                size={24}
                color={theme.colors.palette.primary600}
                style={styles.linkIcon}
              />
              <Text style={[styles.linkText, { color: theme.colors.palette.primary600 }]}>
                Start a confidential chat
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => handlePress("tel:8007994889")}
            >
              <MaterialIcons
                name="hearing"
                size={24}
                color={theme.colors.palette.primary600}
                style={styles.linkIcon}
              />
              <Text style={[styles.linkText, { color: theme.colors.palette.primary600 }]}>
                Call TTY if you have hearing loss <Text style={styles.bold}>800-799-4889</Text>
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => handlePress("https://www.veteranscrisisline.net")}
            >
              <MaterialIcons
                name="language"
                size={24}
                color={theme.colors.palette.primary600}
                style={styles.linkIcon}
              />
              <Text style={[styles.linkText, { color: theme.colors.palette.primary600 }]}>
                Get more resources at <Text style={styles.bold}>VeteransCrisisLine.net</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </SidekickView>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  closeButton: {
    position: "absolute",
    zIndex: 2100,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
  },
  logo: {
    width: "75%",
    height: 120,
    alignSelf: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 16,
    textAlign: "center",
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 24,
    textAlign: "center",
  },
  bold: {
    fontWeight: "bold",
  },
  linksContainer: {
    gap: 12,
  },
  linkButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  linkIcon: {
    marginRight: 12,
  },
  linkText: {
    fontSize: 16,
    flex: 1,
  },
})
